<template>
  <div class="page">
    <div class="container">
      <div class="page-header">
        <h1>Committee Meeting/Highlights</h1>
        <Button class="add-btn" label="Add Meeting" icon="pi pi-plus" @click="openAddModal"></Button>
      </div>

    <!-- Tabs -->
    <div class="meeting-tabs">
      <button 
        class="tab-btn" 
        :class="{ active: activeTab === 'senate' }" 
        @click="MultiSelectTab('senate')"
      >
        Senate
      </button>
      <button 
        class="tab-btn" 
        :class="{ active: activeTab === 'house' }" 
        @click="MultiSelectTab('house')"
      >
        House
      </button>
      <button 
        class="tab-btn" 
        :class="{ active: activeTab === 'other' }" 
        @click="MultiSelectTab('other')"
      >
        Other Invitations
      </button>
    </div>

    <DataTable 
      :value="filteredMeetings" 
      :paginator="true" 
      :rows="10"
      :rowsPerPageOptions="[5, 10, 20, 50]"
      paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
      currentPageReportTemplate="{first} to {last} of {totalRecords}"
      :loading="loading"
      stripedRows
      showGridlines
      responsiveLayout="scroll"
      :globalFilterFields="['chamber', 'committee', 'venue', 'status', 'meeting_type']"
      v-model:filters="filters"
      filterDisplay="menu"
    >
      <template #header>
        <div class="table-header">
          <h3>Committee Meetings ({{ filteredMeetings.length }})</h3>
          <IconField iconPosition="left">
            <InputIcon class="pi pi-search" />
            <InputText v-model="filters['global'].value" placeholder="Search meetings..." />
          </IconField>
        </div>
      </template>
      
      <Column field="chamber" header="Chamber" sortable :showFilterMatchModes="false">
        <template #body="{ data }">
          <span class="chamber-badge" :class="data.chamber.toLowerCase()">{{ data.chamber }}</span>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" placeholder="Search chamber" />
        </template>
      </Column>
      
      <Column field="meeting_date" header="Date" sortable>
        <template #body="{ data }">
          {{ formatDate(data.meeting_date) }}
        </template>
      </Column>
      
      <Column field="meeting_time" header="Time" sortable>
        <template #body="{ data }">
          {{ data.meeting_time }}
        </template>
      </Column>
      
      <Column field="venue" header="Venue" sortable :showFilterMatchModes="false">
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" placeholder="Search venue" />
        </template>
      </Column>
      
      <Column field="committee" header="Committee" sortable :showFilterMatchModes="false">
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" placeholder="Search committee" />
        </template>
      </Column>
      
      <Column field="sub_committee" header="Sub Committee" sortable>
        <template #body="{ data }">
          {{ data.sub_committee || 'N/A' }}
        </template>
      </Column>
      
      <Column field="status" header="Status" sortable :showFilterMatchModes="false">
        <template #body="{ data }">
          <span class="status-badge" :class="getStatusClass(data.status)">{{ data.status }}</span>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" placeholder="Search status" />
        </template>
      </Column>
      
      <Column field="meeting_type" header="Type" sortable :showFilterMatchModes="false">
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" placeholder="Search type" />
        </template>
      </Column>
      
      <Column field="agenda_link" header="Agenda" sortable>
        <template #body="{ data }">
          <a v-if="data.agenda_link" :href="data.agenda_link" target="_blank" class="link-btn">View</a>
          <span v-else>N/A</span>
        </template>
      </Column>
      
      <Column field="display_referrals" header="Referrals" sortable>
        <template #body="{ data }">
          {{ data.display_referrals || 'N/A' }}
        </template>
      </Column>
      
      <Column field="action_item" header="Action Item" sortable>
        <template #body="{ data }">
          {{ data.action_item || 'N/A' }}
        </template>
      </Column>
      
      <Column header="Actions" :exportable="false">
        
        <template #body="{ data }">
          <div class="actions">
            <Button 
              icon="pi pi-pencil" 
              severity="info" 
              size="small"
              @click="openEditModal(data)" 
            />
            <Button 
              icon="pi pi-trash" 
              severity="danger" 
              size="small"
              @click="deleteMeeting(data.id)" 
            />
          </div>
        </template>
      </Column>
    </DataTable>
    </div>

    <Dialog 
      v-model:visible="showModal" 
      :header="isEditing ? 'Edit Meeting' : 'Add Meeting'" 
      :modal="true" 
      :closable="false" 
      :dismissable-mask="false"
      :style="{width: '800px'}"
    >
    
      <form @submit.prevent="saveMeeting" class="modal-form">
        <div class="form-grid">
          <div class="form-group">
            <IftaLabel>
              <Select 
                id="subject_id"
                v-model="form.subject_id" 
                :options="subjects"
                optionLabel="subject_title"
                optionValue="id"
                class="w-full"
                showClear
                filter
                :placeholder="`Subject (${subjects.length} available)`"
              />
              <label for="subject_id">Subject</label>
            </IftaLabel>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <IftaLabel>
                <Select 
                  id="chamber"
                  v-model="v$.chamber.$model" 
                  :options="chamberOptions"
                  optionLabel="label"
                  optionValue="value"
                  :class="{ 'p-invalid': v$.chamber.$error }"
                  class="w-full"
                />
                <label for="chamber">Chamber *</label>
              </IftaLabel>
            </div>
            <div class="form-group">
              <IftaLabel>
                <DatePicker 
                  id="meeting_date"
                  v-model="v$.meeting_date.$model" 
                  :class="{ 'p-invalid': v$.meeting_date.$error }"
                  class="w-full"
                  @date-select="onDateSelect"  
                  :manualInput="false"
                />
                <label for="meeting_date">Date *</label>
              </IftaLabel>
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <IftaLabel>
                <InputText 
                  id="meeting_time"
                  v-model="v$.meeting_time.$model" 
                  :class="{ 'p-invalid': v$.meeting_time.$error }"
                  class="w-full"
                  
                />
                <label for="meeting_time">Time *</label>
              </IftaLabel>
            </div>
            <div class="form-group">
              <IftaLabel>
                <InputText 
                  id="venue"
                  v-model="v$.venue.$model" 
                  :class="{ 'p-invalid': v$.venue.$error }"
                  class="w-full"
                />
                <label for="venue">Venue *</label>
              </IftaLabel>
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <IftaLabel>
                <Select 
                  id="committee"
                  v-model="v$.committee.$model" 
                  :options="filteredCommittees"
                  optionLabel="name"
                  optionValue="name"
                  :class="{ 'p-invalid': v$.committee.$error }"
                  class="w-full"
                />
                <label for="committee">Committee *</label>
              </IftaLabel>
            </div>
            <div class="form-group">
              <IftaLabel>
                <Select 
                  id="sub_committee"
                  v-model="form.sub_committee" 
                  :options="filteredCommittees"
                  optionLabel="name"
                  optionValue="name"
                  class="w-full"
                  showClear
                />
                <label for="sub_committee">Sub Committee</label>
              </IftaLabel>
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <IftaLabel>
                <Select 
                  id="status"
                  v-model="v$.status.$model" 
                  :options="statusOptions"
                  optionLabel="label"
                  optionValue="value"
                  :class="{ 'p-invalid': v$.status.$error }"
                  class="w-full"
                />
                <label for="status">Status *</label>
              </IftaLabel>
            </div>
            <div class="form-group">
              <IftaLabel>
                <Select 
                  id="meeting_type"
                  v-model="v$.meeting_type.$model" 
                  :options="meetingTypeOptions"
                  optionLabel="label"
                  optionValue="value"
                  :class="{ 'p-invalid': v$.meeting_type.$error }"
                  class="w-full"
                />
                <label for="meeting_type">Type of Meeting *</label>
              </IftaLabel>
            </div>
          </div>
          
          <div class="form-group">
            <IftaLabel>
              <InputText 
                id="agenda_link"
                v-model="v$.agenda_link.$model" 
                class="w-full"
              />
              <label for="agenda_link">Agenda Link</label>
            </IftaLabel>
          </div>
          
          <div class="form-group">
            <IftaLabel>
              <MultiSelect 
                  id="referrals"
                  v-model="form.referrals" 
                  :options="offices"
                  optionLabel="code"
                  optionValue="code"
                  class="w-full"
                />
              <label for="referrals">Referrals</label>
            </IftaLabel>
          </div>
          
          <div class="form-group">
            <IftaLabel>
              <Textarea 
                id="action_item"
                v-model="form.action_item" 
                rows="3"
                class="w-full"
              />
              <label for="action_item">Status and Action Item</label>
            </IftaLabel>
          </div>
          <div class="form-group">
            <IftaLabel>
              <InputText 
                id="report_link"
                v-model="form.report_link" 
                class="w-full"
              />
              <label for="report_link">Report Link</label>
            </IftaLabel>
          </div>
        </div>

        
        
        <div class="modal-actions">
          <Button 
            type="button" 
            label="Cancel" 
            severity="secondary" 
            icon="pi pi-times"
            @click="closeModal" 
          />
          <Button 
            type="submit" 
            :label="isEditing ? 'Update Meeting' : 'Create Meeting'" 
            icon="pi pi-check"
            :loading="saving"
          />
        </div>
      </form>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required, url } from '@vuelidate/validators'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import Swal from 'sweetalert2'
import api from '../axios.js'
import moment from 'moment-timezone';

moment.tz.setDefault('Asia/Taipei')


const activeTab = ref('senate')
const meetings = ref([])
const committees = ref([])
const referrals = ref([])
const offices = ref([])
const subjects = ref([])
const showModal = ref(false)
const isEditing = ref(false)
const saving = ref(false)
const loading = ref(false)
const formSubmitted = ref(false)



const filters = ref({
  global: { value: null, matchMode: 'contains' },
  chamber: { operator: 'and', constraints: [{ value: null, matchMode: 'contains' }] },
  venue: { operator: 'and', constraints: [{ value: null, matchMode: 'contains' }] },
  committee: { operator: 'and', constraints: [{ value: null, matchMode: 'contains' }] },
  status: { operator: 'and', constraints: [{ value: null, matchMode: 'contains' }] },
  meeting_type: { operator: 'and', constraints: [{ value: null, matchMode: 'contains' }] }
})

const form = ref({
  id: null,
  chamber: '',
  meeting_date: '',
  meeting_time: '',
  venue: '',
  committee: '',
  sub_committee: '',
  status: '',
  meeting_type: '',
  agenda_link: '',
  referrals: '',
  subject_id: '',
  action_item: '',
  report_link: ''
})

const chamberOptions = [
  { label: 'Senate', value: 'Senate' },
  { label: 'House', value: 'House' },
  { label: 'Other', value: 'Other' }
]

const statusOptions = [
  { label: 'Scheduled', value: 'Scheduled' },
  { label: 'Ongoing', value: 'Ongoing' },
  { label: 'Completed', value: 'Completed' },
  { label: 'Cancelled', value: 'Cancelled' },
  { label: 'Postponed', value: 'Postponed' }
]

const meetingTypeOptions = [
  { label: 'Regular', value: 'Regular' },
  { label: 'Special', value: 'Special' },
  { label: 'Public Hearing', value: 'Public Hearing' },
  { label: 'Technical Working Group', value: 'Technical Working Group' },
  { label: 'Executive Session', value: 'Executive Session' }
]

const filteredMeetings = computed(() => {
  return meetings.value.filter(meeting => {
    if (activeTab.value === 'senate') return meeting.chamber === 'Senate'
    if (activeTab.value === 'house') return meeting.chamber === 'House'
    if (activeTab.value === 'other') return meeting.chamber === 'Other'
    return true
  })
})

const filteredCommittees = computed(() => {
  if (!form.value.chamber) return committees.value
  return committees.value.filter(committee => committee.chamber === form.value.chamber)
})

const loadMeetings = async () => {
  try {
    loading.value = true
    const response = await api.get('/committee-meetings')
    meetings.value = response.data
  } catch (error) {
    console.error('Error loading meetings:', error)
    meetings.value = []
  } finally {
    loading.value = false
  }
}

const loadCommittees = async () => {
  try {
    const response = await api.get('/committees?congress_no=20')
    committees.value = response.data
  } catch (error) {
    console.error('Error loading committees:', error)
    committees.value = []
  }
}


const loadOffices = async () => {
  try {
    const response = await api.get('/offices')
    offices.value = response.data
  } catch (error) {
    console.error('Error loading officess:', error)
    offices.value = []
  }
}


const loadSubjects = async () => {
  try {
    const response = await api.get('/bills-by-subject')
    subjects.value = response.data
    console.log('Loaded subjects:', subjects.value)
  } catch (error) {
    console.error('Error loading subjects:', error)
    subjects.value = []
  }
}


const MultiSelectTab = (tab) => {
  activeTab.value = tab
}

const openAddModal = () => {
  isEditing.value = false
  form.value = {
    id: null,
    chamber: activeTab.value === 'senate' ? 'Senate' : activeTab.value === 'house' ? 'House' : 'Other',
    meeting_date: '',
    meeting_time: '',
    venue: '',
    committee: '',
    sub_committee: '',
    status: '',
    meeting_type: '',
    agenda_link: '',
    referrals: '',
    subject_id: null,
    action_item: '',
    report_link: ''
  }
  showModal.value = true
}

const openEditModal = (meeting) => {
  isEditing.value = true
  form.value = { 
    ...meeting,
    subject_id: meeting.subject_id ? Number(meeting.subject_id) : null
  }
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  formSubmitted.value = false
  saving.value = false
  v$.value.$reset()
}

const rules = {
  chamber: { required },
  meeting_date: { required },
  meeting_time: { required },
  venue: { required },
  committee: { required },
  status: { required },
  meeting_type: { required },
  agenda_link: { url }
}

const v$ = useVuelidate(rules, form)

const onDateSelect = (date) => {
  form.value.meeting_date = moment.tz(date, 'Asia/Taipei').format('YYYY-MM-DD')
}

const onTimeSelect = (time) => {
  form.value.meeting_time = moment.tz(time, 'Asia/Taipei').format('h:mm A')
}

const selectDate = (value) => {
  // 1. value is the JS Date (e.g., Fri Oct 24 2025 00:00:00 GMT-0700)
  
  // 2. Use moment.tz to create a date in the target timezone
  // We extract only the year, month, and day to avoid "leakage" from the local time
  const targetTz = "Asia/Taipei";
  
  const correctedDate = moment.tz({
    year: value.getFullYear(),
    month: value.getMonth(),
    day: value.getDate()
  }, targetTz);

  // 3. Set to start of day to keep it clean
  correctedDate.startOf('day');

  // 4. Update your model
  // .format('YYYY-MM-DD') is best for "Date Only" database columns
  // .toISOString() is best if your backend expects a full UTC timestamp
  v$.value.meeting_date.$model = correctedDate.format('YYYY-MM-DD');
  
  console.log("Date Pinned to TZ:", correctedDate.format());

}
const selectTime = (value) => {
    // Date.prototype.toJSON = function(){ return moment(this).format(); }
    // moment().tz("Asia/Taipei").format();

      // 1. 'value' is the native JS Date from PrimeVue
    // 2. Wrap it in moment
    const selectedMoment = moment(value);

    // 3. Convert to your target timezone (e.g., 'America/New_York')
    // .keepLocalTime() is key if you want the clock time to stay the same 
    // but change the underlying timezone context.
    const correctedTime = moment.tz({
      hour: selectedMoment.hour(),
      minute: selectedMoment.minute()
    }, "Asia/Taipei");

    // 4. Update your model with the formatted string or UTC equivalent
    v$.value.meeting_time.$model = correctedTime.toISOString(); 
    
    console.log("Local Selection:", value);
    console.log("Timezone Corrected (ISO):", correctedTime.toISOString());
}
const saveMeeting = async () => {
  formSubmitted.value = true
  
  const isFormValid = await v$.value.$validate()
  if (!isFormValid) {
    return
  }
  
  const result = await Swal.fire({
    title: 'Confirm Action',
    text: 'Confirming',
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Proceed',
    cancelButtonText: 'Cancel',
    target: document.body
  })
  
  if (!result.isConfirmed) {
    return
  }
  
  try {
    saving.value = true
    const formData = { ...form.value }
    
    if (isEditing.value) {
      await api.put(`/committee-meetings/${form.value.id}`, formData)
    } else {
      await api.post('/committee-meetings', formData)
    }
    
    closeModal()
    loadMeetings()
    
    Swal.fire({
      title: 'Success!',
      text: `Meeting ${isEditing.value ? 'updated' : 'created'} successfully`,
      icon: 'success',
      confirmButtonText: 'OK',
      target: document.body
    })
  } catch (error) {
    console.error('Error saving meeting:', error)
  } finally {
    saving.value = false
  }
}

const deleteMeeting = async (id) => {
  const result = await Swal.fire({
    title: 'Delete Meeting',
    text: 'Are you sure you want to delete this meeting?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#ef4444',
    target: document.body
  })
  
  if (result.isConfirmed) {
    try {
      await api.delete(`/committee-meetings/${id}`)
      loadMeetings()
      
      Swal.fire({
        title: 'Deleted!',
        text: 'Meeting has been deleted successfully',
        icon: 'success',
        confirmButtonText: 'OK',
        target: document.body
      })
    } catch (error) {
      console.error('Error deleting meeting:', error)
    }
  }
}

const formatDate = (date) => {
  if (!date) return 'N/A'
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const formatTime = (time) => {
  if (!time) return 'N/A'
  const timeParts = time.split(':')
  const hours = parseInt(timeParts[0])
  const minutes = timeParts[1]
  const ampm = hours >= 12 ? 'PM' : 'AM'
  const displayHours = hours % 12 || 12
  return `${displayHours}:${minutes} ${ampm}`
}

const getStatusClass = (status) => {
  const classes = {
    'Scheduled': 'scheduled',
    'Ongoing': 'ongoing',
    'Completed': 'completed',
    'Cancelled': 'cancelled',
    'Postponed': 'postponed'
  }
  return classes[status] || 'default'
}

onMounted(() => {
  loadMeetings()
  loadCommittees()
  // loadReferrals()
  loadSubjects()
  loadOffices()
})
</script>

<style scoped>
.page {
  padding: 1rem 0;
  background: var(--p-surface-50);
  min-height: 100vh;
}


.container {
  /* background: rgba(255, 255, 255, 0.95); */
  backdrop-filter: blur(10px);
  border-radius: 20px;
  margin: 1rem;
  padding: 1.5rem;
  /* box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1); */
  width: calc(100vw - 2rem);
  max-width: none;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #1e3a8a;
}

.page-header h1 {
  color: #1e3a8a;
  font-size: 2rem;
  font-weight: 700;
}

.add-btn {
  background: #1e3a8a;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s;
}

.add-btn:hover {
  background: #1e40af;
}

.meeting-tabs {
  display: flex;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #e5e7eb;
}

.tab-btn {
  background: none;
  border: none;
  padding: 1rem 2rem;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  transition: all 0.3s;
}

.tab-btn:hover {
  color: #1e3a8a;
}

.tab-btn.active {
  color: #1e3a8a;
  border-bottom-color: #1e3a8a;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.table-header h3 {
  color: #1e3a8a;
  margin: 0;
}

.chamber-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.chamber-badge.senate {
  background: #dbeafe;
  color: #2563eb;
}

.chamber-badge.house {
  background: #d1fae5;
  color: #065f46;
}

.chamber-badge.other {
  background: #fef3c7;
  color: #d97706;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.scheduled {
  background: #e0e7ff;
  color: #3730a3;
}

.status-badge.ongoing {
  background: #fef3c7;
  color: #d97706;
}

.status-badge.completed {
  background: #d1fae5;
  color: #065f46;
}

.status-badge.cancelled {
  background: #fee2e2;
  color: #dc2626;
}

.status-badge.postponed {
  background: #f3e8ff;
  color: #7c3aed;
}

.link-btn {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
}

.link-btn:hover {
  text-decoration: underline;
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.edit-btn, .delete-btn {
  padding: 0.25rem 0.75rem;
  border: none;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.edit-btn {
  background: #3b82f6;
  color: white;
}

.edit-btn:hover {
  background: #2563eb;
}

.delete-btn {
  background: #ef4444;
  color: white;
}

.delete-btn:hover {
  background: #dc2626;
}

.modal-form {
  padding: 0;
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.5rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group .p-inputtext,
.form-group .p-textarea,
.form-group .p-dropdown,
.form-group .p-datepicker,
.form-group .p-MultiSelect {
  width: 100% !important;
  box-sizing: border-box;
}

.form-group .p-datepicker .p-inputtext,
.form-group .p-dropdown .p-dropdown-label,
.form-group .p-MultiSelect .p-MultiSelect-label {
  width: 100% !important;
}

/* Additional specificity for PrimeVue components */
.form-group .p-component {
  width: 100% !important;
}

.form-group .p-element {
  width: 100% !important;
}

/* Specific rules for MultiSelect component */
.form-group .p-MultiSelect {
  width: 100% !important;
  min-width: 0 !important;
}

.form-group .p-MultiSelect .p-MultiSelect-label {
  width: 100% !important;
}

.form-group .p-MultiSelect .p-MultiSelect-trigger {
  width: auto !important;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
}

@media (max-width: 768px) {
  .committee-meetings-page {
    padding: 1rem;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .modal {
    width: 95%;
  }
}
</style>